import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut } from "../../services/api";
import Button from "../common/Button";

interface UserRow {
  id: number;
  username: string;
  full_name?: string;
  role?: string;
  is_active?: boolean;
  created_at?: string;
}

interface RoleRow {
  id: number;
  name: string;
}

export default function UserManagement() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingUser, setAddingUser] = useState(false);

  const [form, setForm] = useState({
    username: "",
    full_name: "",
    role: "",
    password: "",
  });

  // Password Reset Modal State
  const [resetModalUser, setResetModalUser] = useState<UserRow | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        apiGet("/api/admin/users") as Promise<UserRow[]>,
        apiGet("/api/admin/users/roles") as Promise<RoleRow[]>,
      ]);
      setUsers(Array.isArray(u) ? u : []);
      setRoles(Array.isArray(r) ? r : []);
    } catch (e) {
      console.error("Failed to load users", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.role) {
      alert("Username, password, and role are required.");
      return;
    }
    setAddingUser(true);
    try {
      await apiPost("/api/admin/users", form);
      setForm({ username: "", full_name: "", role: "", password: "" });
      load();
    } catch (e: any) {
      alert(e.message || "Failed to add user");
    } finally {
      setAddingUser(false);
    }
  };

  const handleToggleActive = async (user: UserRow) => {
    try {
      await apiPut(`/api/admin/users/${user.id}/status`, { is_active: !user.is_active });
      load();
    } catch (e) {
      alert("Failed to update user status");
    }
  };

  const handleResetPassword = async () => {
    if (!resetModalUser || !newPassword) return;
    setResetting(true);
    try {
      // The API path is actually "/api/admin/users/password" based on existing code.
      // Modifying from user's sample (`/api/admin/users/${resetModalUser.id}/password`) to match existing logic.
      await apiPost("/api/admin/users/password", {
        username: resetModalUser.username,
        password: newPassword,
      });
      setResetModalUser(null);
      setNewPassword("");
      alert("Password updated securely.");
    } catch (e) {
      alert("Failed to reset password.");
    } finally {
      setResetting(false);
    }
  };

  const inputStyle = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '0.95rem',
    background: '#ffffff',
    width: '100%',
    boxSizing: 'border-box' as const
  };

  return (
    <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', gap: '24px', minHeight: 'calc(100vh - 120px)' }}>
      <div className="mod-window-header" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 0 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            🛡️ Access & Security
          </h2>
          <p className="mod-text-muted">Manage clinic staff, system roles, and account security.</p>
        </div>
      </div>

      {/* Add User Form */}
      <form 
        onSubmit={handleAddUser}
        style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px', 
          padding: '24px', 
          background: '#f8fafc', 
          borderRadius: '16px', 
          border: '1px solid #e2e8f0',
          alignItems: 'end'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)' }}>Username</label>
          <input
            style={inputStyle}
            placeholder="e.g. dr.smith"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s+/g, '') })}
            required
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)' }}>Full Name</label>
          <input
            style={inputStyle}
            placeholder="Dr. John Smith"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            required
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)' }}>Role</label>
          <select
            style={inputStyle}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            required
          >
            <option value="">Select Role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.name}>
                {r.name.charAt(0).toUpperCase() + r.name.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)' }}>Initial Password</label>
          <input
            style={inputStyle}
            type="password"
            placeholder="••••••••"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
        </div>
        <div style={{ paddingBottom: '2px' }}>
          <Button type="submit" variant="primary" isLoading={addingUser} fullWidth style={{ height: '42px' }}>
            + Create Account
          </Button>
        </div>
      </form>

      {/* User Roster */}
      <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: 'var(--mod-shadow-sm)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '16px', textAlign: 'left', color: '#475569', fontWeight: 700 }}>User / Role</th>
                <th style={{ padding: '16px', textAlign: 'center', color: '#475569', fontWeight: 700 }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'center', color: '#475569', fontWeight: 700 }}>Date Added</th>
                <th style={{ padding: '16px', textAlign: 'right', color: '#475569', fontWeight: 700 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && users.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Loading accounts...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No users found in the system.</td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }}>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: 700, color: 'var(--mod-text-main)', fontSize: '1rem' }}>{u.full_name || u.username}</div>
                      <div style={{ color: 'var(--mod-text-muted)', fontSize: '0.8rem', marginTop: '4px' }}>
                        @{u.username} • <span style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{u.role ? (u.role.charAt(0).toUpperCase() + u.role.slice(1)) : 'Unknown'}</span>
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{ 
                        background: u.is_active ? '#dcfce7' : '#fee2e2', 
                        color: u.is_active ? '#166534' : '#991b1b', 
                        padding: '6px 12px', 
                        borderRadius: '20px', 
                        fontSize: '0.75rem', 
                        fontWeight: 800 
                      }}>
                        {u.is_active ? 'ACTIVE' : 'SUSPENDED'}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center', color: 'var(--mod-text-muted)' }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          onClick={() => { setResetModalUser(u); setNewPassword(''); setShowPassword(false); }}
                        >
                          Reset Pass
                        </Button>
                        <Button 
                          variant={u.is_active ? "outline" : "primary"} 
                          size="sm" 
                          onClick={() => handleToggleActive(u)}
                          style={u.is_active ? { borderColor: '#ef4444', color: '#ef4444' } : {}}
                        >
                          {u.is_active ? "Suspend" : "Reactivate"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Blurred Password Reset Modal */}
      {resetModalUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setResetModalUser(null)}>
          <div style={{ width: '100%', maxWidth: '400px', background: '#ffffff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', color: '#0f172a' }}>Force Password Reset</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.9rem', color: '#64748b' }}>
              Updating credentials for <strong>@{resetModalUser.username}</strong>
            </p>

            <div style={{ position: 'relative', marginBottom: '24px' }}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter new secure password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ ...inputStyle, paddingRight: '40px', fontFamily: 'monospace', fontSize: '1rem' }}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                title={showPassword ? "Hide Password" : "Show Password"}
              >
                {showPassword ? "👁️" : "👁️🗨️"}
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button variant="ghost" onClick={() => setResetModalUser(null)} disabled={resetting}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleResetPassword} isLoading={resetting}>
                Confirm Reset
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
